import { useCallback } from 'react';

import { formatCurrencyValue } from '@eika/currency-input';

import useTranslation, { format } from '../../../hooks/useTranslation';
import TranslationsConfigType from '../../../translations/translationType';
import { UseValidateHook } from '../../../utils/validation/validateTypes';
import { PaymentLimitsState } from './state';

interface Warning {
    once?: string;
    monthly?: string;
}

export const validateWarnings =
    (translated: TranslationsConfigType) =>
    (formState: PaymentLimitsState): Warning => {
        const warnings: Warning = {
            once: undefined,
            monthly: undefined,
        };

        const { maxTransactionAmount, maxMonthlyTransactionAmount, bankLimits } = formState;
        if (
            maxTransactionAmount &&
            ((bankLimits?.maxTransactionAmount && maxTransactionAmount > bankLimits?.maxTransactionAmount) ||
                (bankLimits?.maxTransactionAmountInSelfServiceChannel &&
                    maxTransactionAmount > bankLimits?.maxTransactionAmountInSelfServiceChannel))
        ) {
            warnings.once = format(
                translated.updatePaymentLimits.validation.bankRestricted,
                formatCurrencyValue(
                    Math.min(
                        bankLimits?.maxTransactionAmount || 0,
                        bankLimits?.maxTransactionAmountInSelfServiceChannel || 0,
                    ),
                ),
            );
        }
        if (
            maxMonthlyTransactionAmount &&
            ((bankLimits?.maxMonthlyTransactionAmount &&
                maxMonthlyTransactionAmount > bankLimits?.maxMonthlyTransactionAmount) ||
                (bankLimits?.maxMonthlyTransactionAmountInSelfServiceChannel &&
                    maxMonthlyTransactionAmount > bankLimits?.maxMonthlyTransactionAmountInSelfServiceChannel))
        ) {
            warnings.monthly = format(
                translated.updatePaymentLimits.validation.bankRestrictedMonthly,
                formatCurrencyValue(
                    Math.min(
                        bankLimits?.maxMonthlyTransactionAmount || 0,
                        bankLimits?.maxMonthlyTransactionAmountInSelfServiceChannel || 0,
                    ),
                ),
            );
        }
        return warnings;
    };

export interface Error {
    once?: string;
    monthly?: string;
}

const validate =
    (translated: TranslationsConfigType) =>
    (formState: PaymentLimitsState): Error => {
        const errors: Error = {
            once: undefined,
            monthly: undefined,
        };

        const { maxTransactionAmount, maxMonthlyTransactionAmount, bankLimits } = formState;

        if (
            maxTransactionAmount !== undefined &&
            maxMonthlyTransactionAmount !== undefined &&
            maxTransactionAmount > maxMonthlyTransactionAmount
        ) {
            errors.once = translated.updatePaymentLimits.validation.cannotExceedMonthly;
        }

        if (maxTransactionAmount === undefined) {
            errors.once = translated.updatePaymentLimits.validation.fillInField;
        } else if (
            (bankLimits?.maxTransactionAmount && maxTransactionAmount > bankLimits?.maxTransactionAmount) ||
            (bankLimits?.maxTransactionAmountInSelfServiceChannel &&
                maxTransactionAmount > bankLimits?.maxTransactionAmountInSelfServiceChannel)
        ) {
            errors.once = format(
                translated.updatePaymentLimits.validation.bankRestricted,
                formatCurrencyValue(
                    Math.min(
                        bankLimits?.maxTransactionAmount || 0,
                        bankLimits?.maxTransactionAmountInSelfServiceChannel || 0,
                    ),
                ),
            );
        }
        if (maxMonthlyTransactionAmount === undefined) {
            errors.monthly = translated.updatePaymentLimits.validation.fillInField;
        } else if (
            (bankLimits?.maxMonthlyTransactionAmount &&
                maxMonthlyTransactionAmount > bankLimits?.maxMonthlyTransactionAmount) ||
            (bankLimits?.maxMonthlyTransactionAmountInSelfServiceChannel &&
                maxMonthlyTransactionAmount > bankLimits?.maxMonthlyTransactionAmountInSelfServiceChannel)
        ) {
            errors.monthly = format(
                translated.updatePaymentLimits.validation.bankRestrictedMonthly,
                formatCurrencyValue(
                    Math.min(
                        bankLimits?.maxMonthlyTransactionAmount || 0,
                        bankLimits?.maxMonthlyTransactionAmountInSelfServiceChannel || 0,
                    ),
                ),
            );
        }

        return errors;
    };

const useValidate: UseValidateHook<PaymentLimitsState, Error> = () => {
    const translated = useTranslation();
    return useCallback(async (state) => validate(translated)(state), [translated]);
};

export default useValidate;
